import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import excelIcon from '../../assets/excel-icon.svg';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { dateMenuItems } from '../../constants/menuItems';
import { useGetTransactionCountPaymodeQuery, useGetGatewaysQuery, useExportPaymentTypeSuccessRateMutation } from '@/services/dashboardApi';


interface PercentageChartProps {
  mode?: 'date' | 'gateway';
  onMenuItemSelect?: (item: string) => void;
  amcId?: string;
  gatewayOptions?: string[];
}

export const PercentageChart: React.FC<PercentageChartProps> = ({
  mode = 'date',
  onMenuItemSelect,
  amcId,
  gatewayOptions,
}) => {
  // Fetch gateways dynamically if in gateway mode
  const { data: gatewaysData } = useGetGatewaysQuery(amcId, {
    skip: mode !== 'gateway' || !!gatewayOptions,
  });
  const availableGateways = gatewayOptions || gatewaysData?.gateways || [];

  // Use dynamic gateway options if provided, otherwise fall back to hardcoded menuItems
  const menuItems = mode === 'date'
    ? dateMenuItems
    : availableGateways;

  const defaultSelected = mode === 'date' ? 'Select Date' : (menuItems[0] || '');
  const [selectedItem, setSelectedItem] = useState(defaultSelected);
  const [activePeriod, setActivePeriod] = useState<'1W' | '1M' | '6M' | '1Y' | 'All'>('1W');
  const theme = useTheme();

  // Update selectedItem when menuItems changes (e.g., when gatewayOptions loads)
  React.useEffect(() => {
    if (mode === 'gateway' && menuItems.length > 0 && !menuItems.includes(selectedItem)) {
      setSelectedItem(menuItems[0]);
    }
  }, [menuItems, mode, selectedItem]);

  // Build API parameters with optional AMC filter
  const apiParams = React.useMemo(() => {
    const params: any = {
      type: activePeriod,
    };

    // Add gateway parameter only for gateway mode and if a gateway is selected
    if (mode === 'gateway' && selectedItem) {
      params.gateway = selectedItem;
    }

    if (amcId) {
      params.amcId = amcId;
    }
    return params;
  }, [activePeriod, mode, selectedItem, amcId]);

  const { data, isLoading } = useGetTransactionCountPaymodeQuery(apiParams);
  const [exportPaymentTypeSuccessRate, { isLoading: isDownloading }] = useExportPaymentTypeSuccessRateMutation();

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportPaymentTypeSuccessRate(apiParams).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `transaction-count-paymode-${apiParams.type}${apiParams.gateway ? `-${apiParams.gateway}` : ''}.xlsx`;

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const periods = [
    { label: '1W', value: '1W' },
    { label: '1M', value: '1M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' },
  ];

  const handleMenuChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedItem(value);
    onMenuItemSelect?.(value);
  };

  const handlePeriodChange = (period: '1W' | '1M' | '6M' | '1Y' | 'All') => {
    setActivePeriod(period);
  };

  const TimePeriodButton: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
    label,
    active,
    onClick,
  }) => (
    <Button
      onClick={onClick}
      sx={{
        bgcolor: active ? theme.palette.primary.light : theme.palette.background.paper,
        color: active ? theme.palette.primary.main : theme.typography.h1.color,
        border: active ? theme.borders.borderMedium : theme.borders.borderTime,
        borderRadius: '20px',
        minWidth: '60px',
        fontWeight: theme.typography.subtitle1.fontWeight,
        '&:hover': {
          bgcolor: active ? theme.palette.primary.light : theme.palette.primary.light,
        },
      }}
    >
      {label}
    </Button>
  );

  const percentageChartOptions: Highcharts.Options = {
    credits: { enabled: false },
    chart: { type: 'area' },
    title: { text: '' },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      itemStyle: {
        color: theme.typography.subtitle1.color,
        fontSize: theme.typography.subtitle1.fontSize?.toString(),
        fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
        fontFamily: 'Roboto',
      },
      symbolRadius: 50,
      symbolHeight: 10,
      symbolWidth: 10,
      itemDistance: 30,
      itemMarginBottom: 5,
    },
    xAxis: {
      categories:
        activePeriod === '6M' || activePeriod === '1Y'
          ? data?.map((item) => item.MonthName || '') || []
          : data?.map((item) =>
            new Date(item.TransactionDate!).toLocaleDateString('en-US', {
              day: '2-digit',
              month: 'short',
            })
          ) || [],
      lineColor: theme.palette.divider,
    },
    yAxis: {
      title: { text: null },
      labels: { format: '{value}%' },
      min: 0,
      max: 100,
      tickInterval: 20,
      gridLineDashStyle: 'Dash',
      gridLineColor: theme.palette.divider,
    },
    plotOptions: {
      area: {
        fillOpacity: 0.1,
        marker: {
          radius: 4,
          lineWidth: 2,
          symbol: 'circle',
        },
      },
    },
    series: [
      {
        type: 'area',
        name: 'Net Banking',
        data:
          data?.map((item) =>
            parseFloat(
              ((item.NetBankingSuccessCount * 100) / item.TotalSuccessCount || 0).toFixed(2)
            )
          ) || [],
        color: theme.palette.areaChart.netBanking,
      },
      {
        type: 'area',
        name: 'Debit Card',
        data:
          data?.map((item) =>
            parseFloat(
              (
                ((item?.DebitCardSuccessCount || 0) / (item?.TotalSuccessCount || 1)) * 100 || 0
              ).toFixed(2)
            )
          ) || [],
        color: theme.palette.areaChart.debitcard,
      },
      {
        type: 'area',
        name: 'UPI',
        data:
          data?.map((item) =>
            parseFloat(
              (
                ((item?.NetBankingSuccessCount || 0) / (item?.TotalSuccessCount || 1)) * 100 || 0
              ).toFixed(2)
            )
          ) || [],
        color: theme.palette.areaChart.upi,
      },
      {
        type: 'area',
        name: 'Enach Mandates',
        data:
          data?.map((item) =>
            parseFloat(
              (
                ((item?.EnachSuccessCount || 0) / (item?.TotalSuccessCount || 1)) * 100 || 0
              ).toFixed(2)
            )
          ) || [],
        color: theme.palette.areaChart.enachmandates,
      },
      {
        type: 'area',
        name: 'UPI Mandates',
        data:
          data?.map((item) =>
            parseFloat(
              (
                ((item?.UAutoSuccessCount || 0) / (item?.TotalSuccessCount || 1)) * 100 || 0
              ).toFixed(2)
            )
          ) || [],
        color: theme.palette.areaChart.upi,
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px',
        p: 3,
        flex: 1,
        minWidth: '300px',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: theme.typography.h3.fontWeight }}>
            Payment Type Success Rate
          </Typography>
          <Tooltip title="Download Excel">
            <IconButton
              onClick={handleDownload}
              disabled={isDownloading || !data || data.length === 0}
              size="small"
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {isDownloading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <img src={excelIcon} alt="Export" style={{ width: 20, height: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small">
          <Select
            value={selectedItem}
            onChange={handleMenuChange}
            displayEmpty
            inputProps={{ 'aria-label': 'Select gateway' }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                padding: '8px 32px 8px 12px',
              },
            }}
          >
            {menuItems.map((item: any) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={percentageChartOptions} />
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        {periods.map((period) => (
          <TimePeriodButton
            key={period.value}
            label={period.label}
            active={activePeriod === period.value}
            onClick={() => handlePeriodChange(period.value as '1W' | '1M' | '6M' | '1Y' | 'All')}
          />
        ))}
      </Box>
    </Box>
  );
};
